import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Zap, Activity, Bug, Cloud, History } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';
import SaleV2ChatMessagesSF from './SaleV2ChatMessagesSF';
import V2ChatInput from '../V2Chat/V2ChatInput';
import V2EventLog from '../V2Chat/V2EventLog';
import { useSFSaleWorkflow } from './hooks/useSFSaleWorkflow';

/**
 * Sale Order Agent SF (Salesforce) Chat Page
 * Uses same V2 chat system but connects to Salesforce backend
 */
const SaleOrderAgentSFChat = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // Workflow info
  const workflowId = 21; // SF Sale Order workflow ID
  const initialRunId = searchParams.get('runId') || searchParams.get('run_id');
  const viewOnly = searchParams.get('viewOnly') === 'true'; // View-only mode for historical workflows
  const agentId = searchParams.get('agentId'); // Track source for cancel navigation
  const workflowName = 'Sales Order Processing (Salesforce)';

  const [showEventLog, setShowEventLog] = useState(false);

  // SF Sale Workflow hook
  const {
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    sendMessage,
    events,
    error,
    resetWorkflow,
    hasActiveWorkflow
  } = useSFSaleWorkflow(workflowId, token, initialRunId);

  // Cleanup on unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasActiveWorkflow) {
        console.log('🔵 SF: Page unloading - cleanup');
        resetWorkflow();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveWorkflow, resetWorkflow]);

  const handleBack = () => navigate('/workflows');

  const handleDebug = () => {
    if (runId) navigate(`/sale-order-sf-debug?execution=${runId}`);
  };

  const handleHistory = () => {
    navigate('/sale-order-sf-history');
  };

  const getStatusBadge = () => {
    switch (executionStatus) {
      case 'running':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
            Running
          </span>
        );
      case 'awaiting_user_input':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
            Awaiting Input
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
            Failed
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full"></span>
            Ready
          </span>
        );
    }
  };

  if (!token) {
    return (
      <MainLayout title="Sale Order Agent SF">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please log in to access the Salesforce Sale Order Agent.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Sale Order Agent SF">
      {/* Header - Salesforce Blue themed */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-t-lg">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="relative p-2.5 bg-white/20 rounded-xl">
              <Cloud className="w-7 h-7 text-white" />
              <div className="absolute -bottom-1 -right-1 p-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full">
                <Zap className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold">{workflowName}</h1>
              <p className="text-xs opacity-90">PO to Salesforce Opportunity</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {getStatusBadge()}

          {currentStep && (
            <span className="text-xs opacity-75 bg-white/10 px-2 py-1 rounded">
              Step: {currentStep}
            </span>
          )}

          {runId && (
            <span className="text-xs opacity-75 bg-white/10 px-2 py-1 rounded font-mono">
              {runId.slice(0, 8)}...
            </span>
          )}

          <button
            onClick={handleHistory}
            className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            title="View History"
          >
            <History className="w-5 h-5" />
          </button>

          <button
            onClick={() => setShowEventLog(!showEventLog)}
            className={`p-2 rounded-lg transition-colors ${
              showEventLog ? 'bg-white/30' : 'bg-white/20 hover:bg-white/30'
            }`}
            title="Toggle Event Log"
          >
            <Activity className="w-5 h-5" />
          </button>

          {runId && (
            <button
              onClick={handleDebug}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              title="View Debug Logs"
            >
              <Bug className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden h-[calc(100vh-12rem)] bg-white dark:bg-gray-900 rounded-b-lg">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <SaleV2ChatMessagesSF
              key={runId || 'initial'}
              messages={messages}
              isLoading={isLoading}
              workflowName={workflowName}
              error={error}
              runId={runId}
              workflowId={workflowId}
              token={token}
              sendMessage={sendMessage}
              executionStatus={executionStatus}
              currentStep={currentStep}
              viewOnly={viewOnly}
              agentId={agentId}
            />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <V2ChatInput
              onSendMessage={sendMessage}
              isLoading={isLoading}
              placeholder="Upload a purchase order or send a message..."
              disabled={!token}
            />
          </div>
        </div>

        {/* Event Log Sidebar */}
        {showEventLog && (
          <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <V2EventLog
              events={events}
              runId={runId}
              onClose={() => setShowEventLog(false)}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SaleOrderAgentSFChat;
